import { useState, useEffect } from 'react';
import {
  TeCell,
  TeContainer,
  TeContainerSlim,
  TeIcon,
  TeRow,
  TeTable,
  TeText,
  TeToggle,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import {
  type Taxon,
  type Taxonomy,
  type Subscriptions,
} from '../../../api/generated/notification-pref';
import { getChannelsPerClass } from '../../../utils/notifications';

const NotificationSettingsTopicsSection = ({
  taxonomyData,
  subscriptionsData,
  onCheckedChange,
  disabled,
}: {
  taxonomyData?: Taxonomy;
  subscriptionsData?: Subscriptions;
  onCheckedChange: (className: string, value: boolean) => void;
  disabled: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <TeContainer column backgroundColor="background.primary" fill gap="s">
      <TeText weight="bold" size="l">
        {t('notifications.settings.notifyMeAbout')}
      </TeText>
      {taxonomyData &&
        Object.entries(taxonomyData.classes).map(([key, topLevelClass]) => {
          return (
            <NotificationTopicTable
              key={key}
              classKey={key}
              topLevelClass={topLevelClass as Taxon}
              subscriptionsData={subscriptionsData}
              onCheckedChange={onCheckedChange}
              disabled={disabled}
            />
          );
        })}
    </TeContainer>
  );
};

const NotificationTopicTable = ({
  classKey,
  topLevelClass,
  subscriptionsData,
  onCheckedChange,
  disabled,
}: {
  classKey: string;
  topLevelClass: Taxon;
  subscriptionsData?: Subscriptions;
  onCheckedChange: (className: string, value: boolean) => void;
  disabled: boolean;
}) => {
  if (!topLevelClass.classes) return;

  // Get all leaf classes with their channels
  // Keys in the result are the full class identifiers (e.g., "ciam.domain.created")
  const classes = getChannelsPerClass(topLevelClass);

  // Use the name from metadata for display, fallback to classKey
  const topicLabel = topLevelClass.metadata?.name || classKey;

  return (
    <TeTable evenOdd>
      <TeRow>
        <TeCell>
          <TeText color="text.secondary" size="l" weight="bold">
            {topicLabel}
          </TeText>
        </TeCell>
      </TeRow>
      {Object.entries(classes).map(([className, classData]) => {
        return (
          <TeRow key={className}>
            <TeCell>
              <NotificationOptionTypeRow
                className={className}
                channels={classData.channels}
                name={classData.name}
                defaultOn={classData.defaultOn}
                subscriptionsData={subscriptionsData}
                onCheckedChange={onCheckedChange}
                disabled={disabled}
              />
            </TeCell>
          </TeRow>
        );
      })}
    </TeTable>
  );
};

const NotificationOptionTypeRow = ({
  channels,
  className,
  name,
  defaultOn,
  subscriptionsData,
  onCheckedChange,
  disabled,
}: {
  channels: string[];
  className: string;
  name?: string;
  /**
   * Whether this notification class should be enabled by default for new users.
   * - true: defaults to ON
   * - false or undefined: defaults to OFF
   */
  defaultOn?: boolean;
  subscriptionsData?: Subscriptions;
  onCheckedChange: (className: string, value: boolean) => void;
  disabled: boolean;
}) => {
  const subscriptionsClass = subscriptionsData?.classes[className];
  const serverChannels = subscriptionsClass?.channels;

  // Evaluate ChannelMap: check for any true channel value.
  // null means "inherit the class default" — treat it the same as no explicit preference.
  const hasActiveChannel =
    typeof serverChannels === 'object' &&
    serverChannels !== null &&
    Object.values(serverChannels).some((v) => v === true);

  // Determine whether all channel values are null (i.e., no explicit override).
  // Note: Object.values({}).every(...) is vacuously true, so we require at least one key
  // to avoid treating an empty ChannelMap as "all channels inherited".
  const channelEntries =
    typeof serverChannels === 'object' && serverChannels !== null
      ? Object.values(serverChannels)
      : [];
  const allChannelsInherited = channelEntries.length > 0 && channelEntries.every((v) => v === null);

  // Class is ON when explicitly subscribed. When absent, all-null (inherited), or unset,
  // fall back to the taxonomy default_on value.
  const hasExplicitSubscription =
    subscriptionsClass !== undefined && serverChannels !== undefined && !allChannelsInherited;
  const serverChecked = hasExplicitSubscription ? hasActiveChannel : defaultOn === true;

  const [checked, setChecked] = useState(serverChecked);

  // Sync with server data when it updates (e.g., after a successful save or on initial load)
  useEffect(() => {
    setChecked(serverChecked);
  }, [serverChecked]);

  const label = name || className;

  return (
    <TeContainerSlim fill grow justifyContent="space-between" alignItems="center">
      <TeContainerSlim fill>
        <TeText size="m" color={disabled ? 'text.disabled' : 'text.primary'}>
          {label}
        </TeText>
      </TeContainerSlim>
      <TeContainerSlim fill alignItems="center" gap="xs2">
        <TeIcon
          name="public"
          size="m"
          color={channels?.includes('in_app') ? 'text.color' : 'background.disabled'}
        />
        <TeIcon
          name="email"
          size="m"
          color={channels?.includes('email') ? 'text.color' : 'background.disabled'}
        />
        {/* Push notification icon - uncomment when push is supported
        <TeIcon
          name="notifications"
          size="m"
          color={channels?.includes(Channels.Push) ? 'text.color' : 'background.disabled'}
        />
        */}
        {/* SMS channel is currently not supported and will be added at a later time
        <TeIcon
          name="phone_iphone"
          size="m"
          outline
          color={channels?.includes(Channels.Sms) ? 'text.color' : 'background.disabled'}
        />
        */}
        <TeToggle
          size="s"
          toggled={checked}
          disabled={disabled}
          techange={(e) => {
            setChecked(e.target.checked);
            onCheckedChange(className, e.target.checked);
          }}
        />
      </TeContainerSlim>
    </TeContainerSlim>
  );
};

export default NotificationSettingsTopicsSection;
